import React, { useEffect, useRef, useState } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { ChevronLeft, ChevronRight, Heart, Plus, ShoppingBag, Users, Clock, Flame, ChefHat } from 'lucide-react';
import { api } from '../lib/api';
import { useLang } from '../contexts/LanguageContext';
import { useAuth } from '../contexts/AuthContext';
import Timer from '../components/Timer';
import VoiceControl from '../components/VoiceControl';
import VoiceReader from '../components/VoiceReader';
import CookMode from '../components/CookMode';
import DietConvert from '../components/DietConvert';
import ShareButton from '../components/ShareButton';
import RecipeTranslate from '../components/RecipeTranslate';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';
import { maybeShowInterstitial } from '../lib/admob';

export default function RecipeDetail() {
  const { id } = useParams();
  const { t, lang, isRTL } = useLang();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [recipe, setRecipe] = useState(null);
  const [servings, setServings] = useState(1);
  const [stepIdx, setStepIdx] = useState(0);
  const [isFav, setIsFav] = useState(false);
  const [cookOpen, setCookOpen] = useState(false);
  const timerRef = useRef(null);

  useEffect(() => {
    api.get(`/recipes/${id}`).then(({ data }) => {
      setRecipe(data);
      setServings(data.servings);
    });
    // fire-and-forget view tracking
    api.post(`/recipes/${id}/view`).catch(() => {});
    if (user) {
      api.get('/favorites').then(({ data }) => {
        setIsFav(data.some(r => r.recipe_id === id));
      }).catch(() => {});
    }
    // Show an interstitial ad to free users every Nth recipe view.
    // Counter + threshold live inside admob.js. Premium users are skipped.
    maybeShowInterstitial({ isPremium: !!user?.is_premium });
  }, [id, user]);

  if (!recipe) return <div className="py-20 text-center text-[#5C4D44]">...</div>;

  const factor = recipe.servings > 0 ? servings / recipe.servings : 1;
  const title = lang === 'ar' ? recipe.title_ar : recipe.title_en;
  const desc = lang === 'ar' ? recipe.description_ar : recipe.description_en;
  const tools = lang === 'ar' ? recipe.tools_ar : recipe.tools_en;
  const steps = lang === 'ar' ? recipe.steps_ar : recipe.steps_en;

  const scaleQty = (q) => {
    const v = q * factor;
    if (Number.isInteger(v)) return v;
    return Math.round(v * 100) / 100;
  };

  const toggleFav = async () => {
    if (!user) { toast.info(t('needLogin')); navigate('/login'); return; }
    try {
      if (isFav) {
        await api.delete(`/favorites/${id}`);
        setIsFav(false);
      } else {
        await api.post(`/favorites/${id}`);
        setIsFav(true);
      }
    } catch (e) { toast.error('Error'); }
  };

  const addIngrToShopping = async (ing) => {
    if (!user) { toast.info(t('needLogin')); navigate('/login'); return; }
    const name = lang === 'ar' ? ing.name_ar : ing.name_en;
    const unit = lang === 'ar' ? ing.unit_ar : ing.unit_en;
    try {
      await api.post('/shopping-list', { name, qty: `${scaleQty(ing.qty)} ${unit}` });
      toast.success(t('addedToShopping'));
    } catch { toast.error('Error'); }
  };

  const addAllToShopping = async () => {
    if (!user) { toast.info(t('needLogin')); navigate('/login'); return; }
    try {
      await Promise.all(recipe.ingredients.map(ing => {
        const name = lang === 'ar' ? ing.name_ar : ing.name_en;
        const unit = lang === 'ar' ? ing.unit_ar : ing.unit_en;
        return api.post('/shopping-list', { name, qty: `${scaleQty(ing.qty)} ${unit}` });
      }));
      toast.success(t('addedToShopping'));
    } catch { toast.error('Error'); }
  };

  const goNext = () => setStepIdx(i => Math.min(i + 1, steps.length - 1));
  const goPrev = () => setStepIdx(i => Math.max(i - 1, 0));

  const handleVoice = (cmd) => {
    if (cmd === 'next') goNext();
    else if (cmd === 'prev') goPrev();
    else if (cmd === 'start') timerRef.current?.start();
    else if (cmd === 'stop') timerRef.current?.stop();
  };

  const ArrowPrev = isRTL ? ChevronRight : ChevronLeft;
  const ArrowNext = isRTL ? ChevronLeft : ChevronRight;

  return (
    <div className="space-y-8 pb-16" data-testid="recipe-detail">
      <Link to="/" className="inline-flex items-center gap-1 text-sm text-[#5C4D44] hover:text-[#C05A42]" data-testid="back-home">
        <ChevronLeft className="h-4 w-4 rtl:rotate-180" /> {t('backHome')}
      </Link>

      <div className="grid lg:grid-cols-2 gap-8">
        <div className="relative overflow-hidden rounded-3xl aspect-square">
          <img src={recipe.image} alt={title} className="absolute inset-0 w-full h-full object-cover" />
        </div>
        <div className="flex flex-col gap-4">
          <h1 className="text-3xl sm:text-4xl font-display font-bold tracking-tight">{title}</h1>
          <p className="text-[#5C4D44] leading-relaxed">{desc}</p>

          <div className="flex flex-wrap gap-3 text-sm text-[#5C4D44]">
            <span className="inline-flex items-center gap-1 bg-[#EFEBE1] px-3 py-1.5 rounded-full"><Clock className="h-3.5 w-3.5" /> {recipe.prep_time + recipe.cook_time} {t('minutes')}</span>
            <span className="inline-flex items-center gap-1 bg-[#EFEBE1] px-3 py-1.5 rounded-full"><Users className="h-3.5 w-3.5" /> {recipe.servings}</span>
            {recipe?.nutrition?.kcal ? (
              <span className="inline-flex items-center gap-1 bg-[#E29F48] text-white px-3 py-1.5 rounded-full font-semibold">
                <Flame className="h-3.5 w-3.5" /> {Math.round(recipe.nutrition.kcal * factor)} {t('kcal')}
              </span>
            ) : null}
          </div>

          {recipe?.nutrition?.kcal ? (
            <div className="rounded-2xl bg-white border border-black/5 p-4">
              <div className="text-xs uppercase tracking-wider text-[#5C4D44] mb-3 flex items-center justify-between">
                <span>{t('nutrition')} · {t('perServing')}</span>
                <span className="font-semibold text-[#C05A42]">{Math.round(recipe.nutrition.kcal)} {t('kcal')}</span>
              </div>
              <div className="grid grid-cols-3 gap-2 text-center" data-testid="nutrition-breakdown">
                <div className="rounded-xl bg-[#F1F4EE] p-2">
                  <div className="text-base font-display font-bold text-[#4A5D23]">{recipe.nutrition.protein}g</div>
                  <div className="text-xs text-[#5C4D44]">{t('protein')}</div>
                </div>
                <div className="rounded-xl bg-[#F9F1E8] p-2">
                  <div className="text-base font-display font-bold text-[#E29F48]">{recipe.nutrition.carbs}g</div>
                  <div className="text-xs text-[#5C4D44]">{t('carbs')}</div>
                </div>
                <div className="rounded-xl bg-[#F7E8E3] p-2">
                  <div className="text-base font-display font-bold text-[#C05A42]">{recipe.nutrition.fat}g</div>
                  <div className="text-xs text-[#5C4D44]">{t('fat')}</div>
                </div>
              </div>
            </div>
          ) : null}

          <div className="rounded-2xl bg-white border border-black/5 p-5">
            <label className="text-xs uppercase tracking-wider text-[#5C4D44]">{t('servings')}</label>
            <div className="mt-2 flex items-center gap-3">
              <button onClick={() => setServings(s => Math.max(1, s - 1))} data-testid="servings-decrease" className="h-10 w-10 rounded-full bg-[#EFEBE1] hover:bg-[#E3DDCF] font-bold text-lg">−</button>
              <span className="text-2xl font-display font-bold tabular-nums w-12 text-center" data-testid="servings-value">{servings}</span>
              <button onClick={() => setServings(s => s + 1)} data-testid="servings-increase" className="h-10 w-10 rounded-full bg-[#C05A42] text-white hover:bg-[#A04832] font-bold text-lg">+</button>
            </div>
          </div>

          <div className="flex flex-wrap gap-3">
            <Button onClick={toggleFav} data-testid="favorite-toggle" className={`rounded-full ${isFav ? 'bg-[#C05A42]' : 'bg-white border border-black/10 text-[#2C2420] hover:bg-[#EFEBE1] dark:bg-neutral-800 dark:text-neutral-100 dark:border-white/10 dark:hover:bg-neutral-700'}`}>
              <Heart className={`h-4 w-4 me-2 ${isFav ? 'fill-white text-white' : ''}`} />
              {isFav ? t('removeFav') : t('saveFav')}
            </Button>
            <Button onClick={addAllToShopping} data-testid="add-all-shopping" className="rounded-full bg-[#7A8B76] hover:bg-[#62725f] text-white">
              <ShoppingBag className="h-4 w-4 me-2" /> {t('addAll')}
            </Button>
            <ShareButton recipe={recipe} lang={lang} />
          </div>
        </div>
      </div>

      <RecipeTranslate recipe={recipe} onTranslated={setRecipe} />

      {recipe.video_url && (
        <div className="rounded-3xl overflow-hidden aspect-video bg-black">
          <iframe src={recipe.video_url} title={title} className="w-full h-full" allowFullScreen></iframe>
        </div>
      )}

      <section>
        <h2 className="text-2xl font-display font-semibold mb-4">{t('ingredients')}</h2>
        <ul className="grid sm:grid-cols-2 gap-3" data-testid="ingredients-list">
          {recipe.ingredients.map((ing, i) => {
            const name = lang === 'ar' ? ing.name_ar : ing.name_en;
            const unit = lang === 'ar' ? ing.unit_ar : ing.unit_en;
            return (
              <li key={`ing-${ing.name_en || ing.name_ar || i}-${i}`} className="flex items-center justify-between rounded-2xl bg-white border border-black/5 p-4">
                <span className="text-[#2C2420]">
                  <span className="font-display font-semibold tabular-nums text-[#C05A42]">{scaleQty(ing.qty)}</span> {unit} — {name}
                </span>
                <button onClick={() => addIngrToShopping(ing)} data-testid={`add-ingr-${i}`} className="inline-flex items-center justify-center h-8 w-8 rounded-full bg-[#EFEBE1] hover:bg-[#C05A42] hover:text-white transition">
                  <Plus className="h-4 w-4" />
                </button>
              </li>
            );
          })}
        </ul>
      </section>

      <section>
        <h2 className="text-2xl font-display font-semibold mb-4">{t('tools')}</h2>
        <div className="flex flex-wrap gap-2">
          {tools.map((tool, i) => (
            <span key={`tool-${tool}-${i}`} className="bg-[#EFEBE1] text-[#5C4D44] px-4 py-1.5 rounded-full text-sm">{tool}</span>
          ))}
        </div>
      </section>

      <DietConvert recipeId={recipe.recipe_id} />

      {recipe.affiliate_tools?.length ? (
        <section data-testid="affiliate-section">
          <h2 className="text-2xl font-display font-semibold mb-4">{t('buyTools')}</h2>
          <div className="grid sm:grid-cols-2 gap-3">
            {recipe.affiliate_tools.map((item, i) => (
              <div key={item.key || `aff-${i}`} className="rounded-2xl bg-white border border-black/5 p-4" data-testid={`affiliate-${i}`}>
                <div className="font-medium text-[#2C2420] mb-3">{lang === 'ar' ? item.label_ar : item.label_en}</div>
                <div className="flex flex-wrap gap-2">
                  {Object.entries(item.shops || {}).map(([store, url]) => (
                    <a
                      key={store}
                      href={url}
                      target="_blank"
                      rel="noopener noreferrer sponsored"
                      data-testid={`shop-${item.key}-${store}`}
                      className="inline-flex items-center gap-1 rounded-full bg-[#EFEBE1] hover:bg-[#C05A42] hover:text-white text-[#2C2420] text-xs font-semibold px-3 py-1.5 transition"
                    >
                      {store === 'amazon' ? 'Amazon' : store === 'noon' ? 'Noon' : store === 'aliexpress' ? 'AliExpress' : store}
                    </a>
                  ))}
                </div>
              </div>
            ))}
          </div>
          <p className="text-[10px] text-[#5C4D44] mt-2">* {t('affiliateDisclosure')}</p>
        </section>
      ) : null}

      <section>
        <div className="flex items-center justify-between flex-wrap gap-3 mb-4">
          <h2 className="text-2xl font-display font-semibold">{t('steps')}</h2>
          <div className="flex items-center gap-2 flex-wrap">
            <button
              type="button"
              onClick={() => setCookOpen(true)}
              data-testid="cook-mode-btn"
              className="inline-flex items-center gap-2 rounded-full bg-[#2C2420] hover:bg-black text-white px-4 py-2 text-sm font-medium transition shadow-md"
              title={t('cookModeHint')}
            >
              <ChefHat className="h-4 w-4" />
              {t('cookMode')}
            </button>
            <VoiceReader recipeId={recipe.recipe_id} />
          </div>
        </div>
        <div className="rounded-3xl bg-gradient-to-br from-[#EFEBE1] to-[#F9F6F0] dark:from-neutral-800 dark:to-neutral-900 p-6 sm:p-8">
          <div className="text-xs uppercase tracking-wider text-[#5C4D44] dark:text-neutral-400 mb-2 flex items-center justify-between gap-3" data-testid="step-counter">
            <span>{t('step')} {stepIdx + 1} {t('of')} {steps.length}</span>
            <VoiceReader recipeId={recipe.recipe_id} stepIndex={stepIdx} compact />
          </div>
          <p className="text-lg leading-relaxed text-[#2C2420] dark:text-neutral-100 mb-6 min-h-[80px]" data-testid="step-text">{steps[stepIdx]?.text}</p>

          {steps[stepIdx]?.timer ? <Timer ref={timerRef} seconds={steps[stepIdx].timer} /> : null}

          <div className="mt-6 flex items-center justify-between">
            <button onClick={goPrev} disabled={stepIdx === 0} data-testid="step-prev" className="inline-flex items-center gap-1 rounded-full bg-white px-5 py-2.5 text-sm font-medium disabled:opacity-40 hover:bg-[#EFEBE1] transition">
              <ArrowPrev className="h-4 w-4" /> {t('prev')}
            </button>
            <VoiceControl onCommand={handleVoice} />
            <button onClick={goNext} disabled={stepIdx === steps.length - 1} data-testid="step-next" className="inline-flex items-center gap-1 rounded-full bg-[#C05A42] text-white px-5 py-2.5 text-sm font-medium disabled:opacity-40 hover:bg-[#A04832] transition">
              {t('next')} <ArrowNext className="h-4 w-4" />
            </button>
          </div>
        </div>
      </section>

      {cookOpen && <CookMode recipe={recipe} onClose={() => setCookOpen(false)} />}
    </div>
  );
}
